import { useState, useEffect, useCallback, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Bell, ArrowRight, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

interface NovoItemRequisicaoNotificacaoProps {
  onIrParaSeparacao?: (requisicaoId: string) => void;
}

interface RequisicaoInfo {
  id: string;
  cod: string | null;
  requisicao_status_id: string | null;
  filial_id: string | null;
  obra: { id: string; obra: string | null } | null;
  requerente: { id: string; nome: string | null; apelido: string | null } | null;
}

interface NovoItemData {
  id: string;
  qtd: number;
  produto: {
    id: string;
    nome_fantasia: string | null;
    modelo: string | null;
    marca: { id: string; nome: string | null } | null;
    unidade_medida: { id: string; unidade_medida: string | null } | null;
  } | null;
  requisicao: RequisicaoInfo | null;
}

interface Notificacao {
  id: string;
  item: NovoItemData;
  requisicao: RequisicaoInfo;
  timestamp: Date;
}

/**
 * NovoItemRequisicaoNotificacao - Componente global para notificar almoxarife
 * sobre novos itens adicionados em requisições.
 *
 * Deve ser renderizado no nível do App para funcionar globalmente.
 * Notifica usuários que têm permissão de REQUISICAO.
 *
 * Escuta em tempo real:
 * - INSERTs na tabela requisicao_conteudo
 * - Notifica em QUALQUER status, EXCETO "Construindo"
 * - FILTRADO por filial_id do usuário (otimização)
 */

// ID do status "Construindo" - NÃO notificar quando estiver neste status
const STATUS_CONSTRUINDO = '66208476-7dba-4a3e-8654-d1620a41b896';

// Debounce delay para evitar múltiplas notificações em sequência rápida
const DEBOUNCE_DELAY = 300;

export default function NovoItemRequisicaoNotificacao({ onIrParaSeparacao }: NovoItemRequisicaoNotificacaoProps) {
  const { pessoa, hasPermission } = useAuth();
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([]); // Fila de notificações pendentes
  const [notificacaoAtual, setNotificacaoAtual] = useState<Notificacao | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingNotificacoesRef = useRef<Notificacao[]>([]);

  // Verificar se o usuário tem permissão de REQUISICAO
  const canAccessRequisicao = hasPermission('REQUISICAO');

  // Filial do usuário para filtrar notificações
  const userFilialId = pessoa?.filial_id;

  // Função para processar notificações com debounce
  const processarNotificacoes = useCallback(() => {
    if (pendingNotificacoesRef.current.length === 0) return;

    setNotificacoes(prev => {
      const novas = pendingNotificacoesRef.current.filter(
        n => !prev.some(existing => existing.id === n.id)
      );
      pendingNotificacoesRef.current = [];
      return [...prev, ...novas];
    });
  }, []);

  // Real-time subscription para detectar novos itens em requisições em andamento
  useEffect(() => {
    // Se não tem permissão ou filial, não iniciar subscription
    if (!canAccessRequisicao || !userFilialId) return;

    const channel = supabase
      .channel(`requisicao_conteudo_filial_${userFilialId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'requisicao_conteudo',
          // Filtrar por filial_id para reduzir tráfego
          filter: `filial_id=eq.${userFilialId}`
        },
        async (payload) => {
          // Buscar dados completos do novo item e da requisição
          const { data: novoItem, error } = await supabase
            .from('requisicao_conteudo')
            .select(`
              *,
              produto:produto_id(id, nome_fantasia, modelo, marca:marca_id(id, nome), unidade_medida:unidade_medida_id(id, unidade_medida)),
              requisicao:requisicao_id(
                id,
                cod,
                requisicao_status_id,
                filial_id,
                obra:obra_id(id, obra),
                requerente:requerente_id(id, nome, apelido)
              )
            `)
            .eq('id', payload.new.id)
            .single();

          if (error || !novoItem) return;

          // DB cast: Supabase nested select returns a structurally compatible shape;
          // narrow to our local NovoItemData view.
          const item = novoItem as unknown as NovoItemData;

          // Verificação adicional de filial (double-check)
          if (item.requisicao?.filial_id !== userFilialId) return;

          // NÃO notificar se a requisição estiver em status "Construindo"
          const statusRequisicao = item.requisicao?.requisicao_status_id;
          if (statusRequisicao === STATUS_CONSTRUINDO) return;

          // Criar notificação
          const notificacao: Notificacao = {
            id: item.id,
            item,
            requisicao: item.requisicao,
            timestamp: new Date()
          };

          // Adicionar à lista pendente e agendar processamento com debounce
          pendingNotificacoesRef.current.push(notificacao);

          // Limpar timer anterior e criar novo
          if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
          }
          debounceTimerRef.current = setTimeout(processarNotificacoes, DEBOUNCE_DELAY);
        }
      )
      .subscribe((status) => {
      });

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      supabase.removeChannel(channel);
    };
  }, [canAccessRequisicao, userFilialId, processarNotificacoes]);

  // Processar fila de notificações - mostrar uma de cada vez
  useEffect(() => {
    if (!notificacaoAtual && notificacoes.length > 0) {
      setNotificacaoAtual(notificacoes[0]);
      setNotificacoes(prev => prev.slice(1));
    }
  }, [notificacoes, notificacaoAtual]);

  // Handler: Apenas dar ciência
  const handleCiente = useCallback(() => {
    setNotificacaoAtual(null);
  }, []);

  // Handler: Ir para separação
  const handleIrParaSeparacao = useCallback(() => {
    if (notificacaoAtual && onIrParaSeparacao) {
      onIrParaSeparacao(notificacaoAtual.requisicao.id);
    }
    setNotificacaoAtual(null);
  }, [notificacaoAtual, onIrParaSeparacao]);

  // Não renderizar se não tem permissão ou não houver notificação
  if (!canAccessRequisicao || !notificacaoAtual) return null;

  const { item, requisicao } = notificacaoAtual;
  const qtd = item.qtd < 10 ? `0${item.qtd}` : item.qtd;
  const unidade = item.produto?.unidade_medida?.unidade_medida || 'Un';
  const marca = item.produto?.marca?.nome || '-';
  const modelo = item.produto?.modelo || '-';
  const nomeProduto = item.produto?.nome_fantasia || '-';
  const obraNome = requisicao?.obra?.obra || '-';
  const requerenteNome = requisicao?.requerente?.apelido || requisicao?.requerente?.nome || '-';

  return createPortal(
    <div style={STYLES.overlay}>
      <div style={STYLES.modal}>
        {/* Ícone de alerta */}
        <div style={STYLES.iconContainer}>
          <Bell size={32} color="#154680" />
        </div>

        {/* Título */}
        <h3 style={STYLES.title}>Novo Item Adicionado!</h3>

        {/* Info da Requisição */}
        <div style={STYLES.requisicaoInfo}>
          <span style={STYLES.requisicaoCod}>{requisicao?.cod}</span>
          <span style={STYLES.requisicaoSeparator}>•</span>
          <span style={STYLES.requisicaoText}>{obraNome}</span>
          <span style={STYLES.requisicaoSeparator}>•</span>
          <span style={STYLES.requisicaoText}>{requerenteNome}</span>
        </div>

        {/* Card do novo item */}
        <div style={STYLES.itemCard}>
          <div style={STYLES.itemQtd}>{qtd} {unidade}</div>
          <div style={STYLES.itemInfo}>
            <div style={STYLES.itemMarca}>{marca} | {modelo}</div>
            <div style={STYLES.itemNome}>{nomeProduto}</div>
          </div>
        </div>

        {/* Mensagem */}
        <p style={STYLES.message}>
          Um novo item foi adicionado à requisição pelo Joshua Ops.
          Esta requisição já está em andamento.
        </p>

        {/* Botões */}
        <div style={STYLES.buttons}>
          <button
            style={STYLES.btnCiente}
            onClick={handleCiente}
          >
            <Check size={16} />
            Ciente
          </button>
          <button
            style={STYLES.btnIr}
            onClick={handleIrParaSeparacao}
          >
            Ir para Separação
            <ArrowRight size={16} />
          </button>
        </div>

        {/* Indicador de mais notificações na fila */}
        {notificacoes.length > 0 && (
          <div style={STYLES.queueIndicator}>
            +{notificacoes.length} {notificacoes.length === 1 ? 'notificação pendente' : 'notificações pendentes'}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

// ============= ESTILOS =============

const STYLES: Record<string, CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10010, // Acima de tudo
    padding: '20px'
  },
  modal: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    width: '480px',
    maxWidth: '95vw',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '3px solid #154680',
    animation: 'slideIn 0.3s ease-out'
  },
  iconContainer: {
    width: '72px',
    height: '72px',
    background: '#eff6ff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    animation: 'pulse 2s infinite'
  },
  title: {
    margin: '0 0 16px 0',
    fontSize: '20px',
    fontWeight: 600,
    color: '#111827'
  },
  requisicaoInfo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  requisicaoCod: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#154680',
    background: '#eff6ff',
    padding: '4px 12px',
    borderRadius: '12px'
  },
  requisicaoSeparator: {
    color: '#9ca3af'
  },
  requisicaoText: {
    fontSize: '13px',
    color: '#6b7280'
  },
  itemCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '16px',
    background: '#f9fafb',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    marginBottom: '20px',
    textAlign: 'left'
  },
  itemQtd: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#154680',
    minWidth: '70px',
    flexShrink: 0
  },
  itemInfo: {
    flex: 1,
    minWidth: 0
  },
  itemMarca: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '2px'
  },
  itemNome: {
    fontSize: '12px',
    color: '#6b7280',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  message: {
    margin: '0 0 24px 0',
    fontSize: '14px',
    color: '#6b7280',
    lineHeight: '1.5'
  },
  buttons: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center'
  },
  btnCiente: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  btnIr: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: '#154680',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  queueIndicator: {
    marginTop: '16px',
    fontSize: '12px',
    color: '#9ca3af',
    fontStyle: 'italic'
  }
};
